import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Modal, Input, Select, Button } from '../ui';
import { staffProfileApi, ApiError } from '../../api';
import { US_STATES } from '../../constants/usStates';

/**
 * Collects the employment/payroll details a provider has requested.
 *
 * SECURITY: every value here — SSN especially — stays inside this component's
 * react-hook-form state and is dropped when the modal unmounts. It must never be
 * lifted into a zustand store (those run `devtools`, which streams state to the
 * Redux DevTools extension) or logged. Do not add `@hookform/devtools`; that
 * package exists to pipe form values into a devtools panel.
 */

const MAX_AGE_YEARS = 120;

const today = () => new Date(new Date().toDateString());

const infoRequestSchema = z.object({
	fullName: z.string().trim().min(2, 'Full name is required').max(100, 'Full name is too long'),
	dateOfBirth: z
		.string()
		.min(1, 'Date of birth is required')
		.refine(value => !Number.isNaN(new Date(value).getTime()), 'Enter a valid date')
		.refine(value => new Date(value) <= today(), 'Date of birth cannot be in the future')
		.refine(value => {
			const earliest = new Date(today());
			earliest.setFullYear(earliest.getFullYear() - MAX_AGE_YEARS);
			return new Date(value) >= earliest;
		}, 'Enter a valid date of birth'),
	ssn: z.string().regex(/^\d{3}-\d{2}-\d{4}$/, 'Enter a valid 9-digit SSN'),
	streetAddress: z
		.string()
		.trim()
		.min(3, 'Street address is required')
		.max(150, 'Street address is too long'),
	city: z.string().trim().min(1, 'City is required').max(85, 'City is too long'),
	state: z.string().min(1, 'State is required'),
	zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid zip code (e.g., 62701)'),
});

type InfoRequestFormData = z.infer<typeof infoRequestSchema>;

/** Progressively formats digits as ###-##-#### while typing. */
const formatSsn = (raw: string) => {
	const digits = raw.replace(/\D/g, '').slice(0, 9);
	if (digits.length <= 3) return digits;
	if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
	return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

interface StaffInfoRequestModalProps {
	isOpen: boolean;
	/** "Remind me Later" — defers for this session only. */
	onRemindLater: () => void;
	onCompleted: () => void;
	businessName?: string | null;
}

const StaffInfoRequestModal = ({
	isOpen,
	onRemindLater,
	onCompleted,
	businessName,
}: StaffInfoRequestModalProps) => {
	const [showSsn, setShowSsn] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid, isSubmitting },
	} = useForm<InfoRequestFormData>({
		resolver: zodResolver(infoRequestSchema),
		// AC3: Submit stays disabled until every required field is valid.
		mode: 'onChange',
		defaultValues: {
			fullName: '',
			dateOfBirth: '',
			ssn: '',
			streetAddress: '',
			city: '',
			state: '',
			zipCode: '',
		},
	});

	const ssnField = register('ssn');

	const onSubmit = async (data: InfoRequestFormData) => {
		try {
			await staffProfileApi.submitMyInfo({
				fullName: data.fullName.trim(),
				dateOfBirth: data.dateOfBirth,
				ssn: data.ssn,
				streetAddress: data.streetAddress.trim(),
				city: data.city.trim(),
				state: data.state,
				zipCode: data.zipCode.trim(),
			});
			toast.success('Thanks! Your information has been submitted.');
			onCompleted();
		} catch (err) {
			// Deliberately NOT surfacing apiError.message here, unlike everywhere
			// else in the app. That message is server-controlled, and a validation
			// error that quotes the offending value ("Invalid SSN 123-45-6789")
			// would put an SSN straight into a toast. The form is fully validated
			// client-side, so a generic message costs little.
			if (import.meta.env.DEV) {
				// eslint-disable-next-line no-console
				console.error('Info-request submit failed:', (err as ApiError).status);
			}
			toast.error('Failed to submit your information. Please try again.');
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onRemindLater}
			title="We need some information from you"
			size="md"
		>
			<p className="text-sm text-gray-500 dark:text-gray-400 mb-5 leading-relaxed">
				{businessName ? `${businessName} has` : 'Your administrator has'} requested the
				following information. Please provide the details below to continue.
			</p>

			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4" autoComplete="off">
				<Input
					label="Full Name"
					required
					placeholder="Jane A. Doe"
					autoComplete="off"
					helperText="As it appears on your Social Security card."
					error={errors.fullName?.message}
					disabled={isSubmitting}
					{...register('fullName')}
				/>

				<Input
					label="Date of Birth"
					type="date"
					required
					max={new Date().toISOString().split('T')[0]}
					autoComplete="off"
					error={errors.dateOfBirth?.message}
					disabled={isSubmitting}
					{...register('dateOfBirth')}
				/>

				<div>
					<div className="flex items-center justify-between mb-2">
						<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
							Social Security Number
							<span className="text-red-500 ml-1">*</span>
						</label>
						<button
							type="button"
							onClick={() => setShowSsn(value => !value)}
							className="text-xs font-semibold text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
						>
							{showSsn ? 'Hide' : 'Show'}
						</button>
					</div>
					<Input
						type={showSsn ? 'text' : 'password'}
						inputMode="numeric"
						placeholder="123-45-6789"
						autoComplete="off"
						error={errors.ssn?.message}
						disabled={isSubmitting}
						{...ssnField}
						onChange={event => {
							event.target.value = formatSsn(event.target.value);
							ssnField.onChange(event);
						}}
					/>
				</div>

				<Input
					label="Street Address"
					required
					placeholder="123 Main St"
					autoComplete="off"
					error={errors.streetAddress?.message}
					disabled={isSubmitting}
					{...register('streetAddress')}
				/>

				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
					<Input
						label="City"
						required
						placeholder="Springfield"
						autoComplete="off"
						error={errors.city?.message}
						disabled={isSubmitting}
						{...register('city')}
					/>
					<Select
						label="State"
						required
						error={errors.state?.message}
						disabled={isSubmitting}
						{...register('state')}
					>
						<option value="">Select</option>
						{US_STATES.map(s => (
							<option key={s.value} value={s.value}>
								{s.label}
							</option>
						))}
					</Select>
					<Input
						label="Zip Code"
						required
						inputMode="numeric"
						placeholder="62701"
						autoComplete="off"
						error={errors.zipCode?.message}
						disabled={isSubmitting}
						{...register('zipCode')}
					/>
				</div>

				<div className="flex items-start gap-2 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2.5">
					<ShieldCheckIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
					<p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
						This information is sent securely and used only for employment and payroll
						records.
					</p>
				</div>

				<div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
					<Button
						type="button"
						variant="secondary"
						onClick={onRemindLater}
						disabled={isSubmitting}
					>
						Remind me Later
					</Button>
					<Button type="submit" isLoading={isSubmitting} disabled={!isValid}>
						Submit
					</Button>
				</div>
			</form>
		</Modal>
	);
};

export default StaffInfoRequestModal;
